import { useEffect } from "react";

import { FaInfoCircle, FaRegQuestionCircle } from "react-icons/fa";
import { useLocalStorage } from "usehooks-ts";

import { Link } from "@chakra-ui/next-js";
import { Flex, HStack, ListItem, Text, Tooltip, UnorderedList, useDisclosure } from "@chakra-ui/react";

import ConfirmModal from "@/components/shared/ConfirmModal";
import ExternalLink from "@/components/shared/ExternalLink";
import localStorageKeys from "@/enums/localStorageKeys";
import useReadySearchParams from "@/hooks/useReadySearchParams";
import { subscriptionPath } from "@/utils/pathGenerators";

export default function SandboxModeBanner() {
  const { workspaceId } = useReadySearchParams();
  const [freeTrialInfoSeen, setFreeTrialInfoSeen] = useLocalStorage(
    localStorageKeys.SANDBOX_INFO_SEEN,
    false
  );

  const disclosureProps = useDisclosure();
  const { onClose, onOpen } = disclosureProps;

  const handleClose = () => {
    setFreeTrialInfoSeen(true);
    onClose();
  };

  const handlePlansClick = () => {
    onClose();
  };

  useEffect(() => {
    if (!freeTrialInfoSeen) {
      onOpen();
    }
  }, [onOpen, freeTrialInfoSeen]);

  return (
    <Flex
      alignItems="center"
      background="background.darkest"
      borderBottom="1px solid var(--chakra-colors-border-base)"
      justifyContent="center"
      py={0.5}
      w="full"
    >
      <ConfirmModal
        {...disclosureProps}
        hideCancel
        description={
          <UnorderedList display="flex" flexDir="column" rowGap={4} textAlign="left">
            <ListItem>
              <Text>
                For AI workloads during your free trial, you&apos;ll have access to one{" "}
                <ExternalLink
                  display="inline-block"
                  href="https://instances.vantage.sh/aws/ec2/g5.2xlarge"
                  text="G5.2xlarge"
                />{" "}
                with 8 vCPUS, 32 GB of memory and 1 A10 GPU with 24GB of VRAM.
              </Text>
            </ListItem>
            <ListItem>
              <Text>
                For traditional workloads, you&apos;ll have access to up to 2 CPU cores and 16GB of memory for
                services like web servers, databases, etc.
              </Text>
            </ListItem>
            <ListItem>
              <Text>Your free trial includes 5 environment hours</Text>
            </ListItem>
            <ListItem>
              <Text>
                Once your free trial expires, you&apos;ll need to add a credit card and upgrade to our Release
                Cloud or Enterprise - Self Hosted plans. View our plans{" "}
                <Link href={subscriptionPath({ workspaceId })} onClick={handlePlansClick}>
                  here
                </Link>
              </Text>
            </ListItem>
          </UnorderedList>
        }
        header="Free Trial"
        icon={FaInfoCircle}
        onConfirm={handleClose}
      />
      <HStack
        bg="background.base"
        borderColor="border.darker"
        borderRadius="3xl"
        borderWidth="1px"
        px={3}
        py={1}
        spacing={1.5}
        w="fit-content"
      >
        <Text
          color="text.primary"
          fontSize="md"
          fontWeight="bolder"
          textAlign="center"
          verticalAlign="center"
        >
          Free Trial
        </Text>
        <Tooltip label="Click to read about your free trial">
          <Text color="blue.500" onClick={onOpen}>
            <span>
              <FaRegQuestionCircle />
            </span>
          </Text>
        </Tooltip>
      </HStack>
    </Flex>
  );
}
